import { useState } from "react";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { purgeTestData } from "../lib/api";
import { toast } from "sonner";
import { PhotoRetentionRow } from "./PhotoRetentionRow";

/**
 * Admin-only "Danger Zone" card.
 *
 * Currently exposes a single one-click action: **Purge Test Data**.
 *  - Deletes every job whose `customer_name` starts with `TEST_` (case-insensitive).
 *  - Cascades cleanup: photos folder, NTS paperwork, sent_emails audit rows,
 *    impound_letters.
 *  - Real ServiceNow/AA jobs that merely mention "test" in their notes are
 *    untouched — the match is anchored to the start of the customer name.
 *
 * Backend endpoint: `POST /api/admin/purge-test-data` (require_admin).
 */
export default function DangerZoneCard() {
  const [purging, setPurging] = useState(false);

  const handlePurge = async () => {
    setPurging(true);
    try {
      const res = await purgeTestData();
      const n = res?.jobs ?? 0;
      if (n === 0) {
        toast.success("No test jobs to purge — DB is clean.");
      } else {
        toast.success(
          `Purged ${n} test job${n === 1 ? "" : "s"}` +
            (res?.sent_emails ? ` · ${res.sent_emails} email audit row${res.sent_emails === 1 ? "" : "s"}` : "") +
            (res?.impound_letters ? ` · ${res.impound_letters} impound letter${res.impound_letters === 1 ? "" : "s"}` : "") +
            (res?.upload_dirs ? ` · ${res.upload_dirs} upload folder${res.upload_dirs === 1 ? "" : "s"}` : ""),
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to purge test data");
    } finally {
      setPurging(false);
    }
  };


  return (
    <div
      className="bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-900/60 rounded-md p-6"
      data-testid="danger-zone-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="h-5 w-5 text-rose-600 dark:text-rose-400" />
        <h2 className="font-display text-lg font-black text-slate-800 dark:text-slate-100">
          Danger Zone
        </h2>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
        Destructive actions for admins only. These cannot be undone.
      </p>

      <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Purge Test Data
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Removes every job whose customer name starts with{" "}
            <code className="font-mono text-[11px] bg-slate-200 dark:bg-slate-700 px-1 rounded">
              TEST_
            </code>{" "}
            (plus their photos, paperwork, and email audit rows). Real jobs
            that merely mention &quot;test&quot; in notes are untouched.
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="destructive"
              disabled={purging}
              className="shrink-0"
              data-testid="purge-test-data-btn"
            >
              {purging ? (
                <Loader2 className="h-4 w-4.5 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4.5" />
              )}
              Purge Test Data
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="purge-test-data-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Purge all test jobs?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently deletes every job in the database whose
                customer name starts with{" "}
                <code className="font-mono">TEST_</code> — along with their
                photos, paperwork, and email audit rows. Real customer jobs are
                not affected. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="purge-cancel-btn">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handlePurge}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="purge-confirm-btn"
              >
                Yes, purge
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <PhotoRetentionRow />

    </div>
  );
}
